import {onLoginUser, UserInterface} from "../../core/interfaces/user-interface";
import {AuthoritiesConstants, UPermission} from "../../core/enums/UPermission";
import {inject, Injector, Service} from "@angular/core";
import {Router} from "@angular/router";
import {BehaviorSubject, Observable} from "rxjs";
import {LayoutService} from "../../layout/service/layout.service";
import {DateRangePickerService} from "./date-range-picker.service";
import {URoleGroup} from "../../core/enums/URoleGroup";
import {UserResourceService} from "../../core/resources/user-resource.service";
import {UserTitleVM} from "../../core/interfaces/UserVM";
import {AccountResourceService} from "../../core/resources/account-resource.service";
import {ManagedUserVM} from "../../core/interfaces/ManagedUserVM";
import {tcRoutes} from "../../../tc.routes";
import {CookieService} from "../../core/services/cookie.service";

/**
 * Сервис текущего пользователя приложения.
 *
 * Назначение:
 * - Хранит и управляет состоянием аутентификации, профилем пользователя, его ролями, правами и группами.
 * - Делегирует операции, связанные с пользователем, в слой API (получение текущего пользователя, смена/сброс пароля).
 * - Управляет навигацией при недостаточности прав и процессом выхода из системы с полным сбросом состояния UI.
 *
 * Основные обязанности:
 * - onLogin: инициализация локального состояния после успешной аутентификации.
 * - getPermissionLevel: расчет числового уровня доступа по разделу на основе выданных ролей/прав.
 * - checkGroup: проверка принадлежности пользователя к группе.
 * - logout: очистка локального состояния, маршрутов верхнего уровня и сервисов UI, переход на страницу логина.
 * - getCurrentUser / changePassword / resetPassword / sendResetPasswordEmail: работа с профилем и паролями через API.
 *
 * Поведение и детали реализации:
 * - Предоставляется на уровне корня (providedIn: 'root'), тем самым является синглтоном в приложении.
 * - Состояние пользователя инкапсулировано в приватных полях; взаимодействие — через геттеры и методы.
 * - При отсутствии достаточных прав выполняется навигация на страницу "application/forbidden-error".
 * - При выходе из системы очищается локальное хранилище, сбрасывается конфигурация маршрутов и состояние зависимых сервисов.
 *
 * Уровни доступа:
 * - 0 — доступа нет (или произведена навигация на страницу запрета).
 * - 1 — базовый доступ (обычно READ).
 * - 2 — расширенный доступ (обычно WRITE).
 * - 3 — доступ на удаление (DELETE).
 * - 4 — полный доступ (FULL_ACCESS).
 *
 * Типичные сценарии использования:
 * - После успешного логина вызвать onLogin(...) для заполнения состояния.
 * - Для получения актуальной информации о профиле — getCurrentUser().
 * - Для смены пароля — changePassword(oldPassword, newPassword).
 * - Для восстановления пароля — sendResetPasswordEmail(email) и resetPassword(resetKey, password).
 */
@Service()
export class UserImpl implements UserInterface {
  private readonly router: Router = inject(Router)
  private readonly userResourceService: UserResourceService = inject(UserResourceService)
  private readonly accountResourceService: AccountResourceService = inject(AccountResourceService)
  private readonly injector: Injector = inject(Injector)
  #cookieService = inject(CookieService)

  private _login!: string;
  private _token!: string;
  private _email!: string;
  private _avatarUrl!: string;
  private _userName!: string;
  private _permissions!: Record<keyof typeof AuthoritiesConstants, (keyof typeof UPermission)[]>;
  private _expire!: number;
  private _id!: number;
  readonly id$ = new BehaviorSubject<number>(0);
  private _groups!: (keyof typeof URoleGroup)[]
  private _isLogin!: boolean

  get id(): number {
    return this._id;
  }

  set id(value: number) {
    this._id = value;
    this.id$.next(value);
  }

  get expire(): number {
    return this._expire;
  }

  get email(): string {
    return this._email;
  }

  set email(value: string) {
    this._email = value;
  }

  get avatarId(): string {
    return this._avatarUrl;
  }

  set avatarId(value: string) {
    this._avatarUrl = value;
  }

  get login(): string {
    return this._login;
  }

  set login(value: string) {
    this._login = value;
  }

  set expire(value: number) {
    this._expire = value;
  }

  get permissions(): Record<keyof typeof AuthoritiesConstants, (keyof typeof UPermission)[]> {
    return this._permissions;
  }

  set permissions(value: Record<keyof typeof AuthoritiesConstants, (keyof typeof UPermission)[]>) {
    this._permissions = value;
  }

  get token(): string {
    return this._token;
  }

  set token(value: string) {
    this._token = value;
  }

  get userName(): string {
    return this._userName;
  }

  set userName(value: string) {
    this._userName = value;
  }

  get groups(): (keyof typeof URoleGroup)[] {
    return this._groups;
  }

  set groups(value: (keyof typeof URoleGroup)[]) {
    this._groups = value;
  }

  get isLogin(): boolean {
    return this._isLogin;
  }

  onLogin(data: onLoginUser): void {
    this._id = data.id
    this.id$.next(data.id)
    this._expire = data.expire
    this._login = data.login
    this._permissions = data.permissions
    this._token = data.token
    this._userName = data.userName
    this._isLogin = true
  }

  getCurrentUser(): Observable<UserTitleVM> {
    return this.userResourceService.currentUser();
  }

  resetPassword(managedUserVM: ManagedUserVM): Observable<any> {
    return this.accountResourceService.resetPassword(managedUserVM);
  }

  initResetPassword(email: string): Observable<void> {
    return this.accountResourceService.resetPasswordInit(email);
  }

  public getPermissionLevel(partition: keyof typeof AuthoritiesConstants): number {
    if (this.permissions[partition] && this.permissions[partition].length > 0) {
      let replacedSectionName: string = partition.replace('_P', '')

      let findPerm: (perm: string) => boolean = perm => this.permissions[partition].includes(replacedSectionName.concat('_' + perm) as keyof typeof UPermission)

      return this.permissions[partition].includes('FULL_ACCESS') ? 4 : findPerm('DELETE') ? 3 : findPerm('WRITE') ? 2 : 1
    }

    if (this.permissions.ROLE_P_FULL_ACCESS) {
      return 4;
    }

    this.router.navigateByUrl('application/forbidden-error').then()

    return 0
  }

  public checkGroup(groupId: number): boolean {
    // return this._groups.includes(URoleGroup[URoleGroup[groupId]])
    return true;
  }

  public rememberToken(token: string) {
    this.#cookieService.setCookie('auth', token, new Date(Number.parseInt(JSON.parse(atob(token.split('.')[1])).exp.toString() + '000')))
  }

  public logout(arrow?: () => void): void {
    this.#cookieService.clearCookie('auth');
    this.router.resetConfig(tcRoutes)
    this.router.navigateByUrl('/login').then(arrow).then(() => this.resetAppState())
  }

  private resetAppState(): void {
    (this.injector.get(LayoutService) as any).reset()
    (this.injector.get(DateRangePickerService) as any).reset()
    this.reset()
  }

  private reset(): void {
    this._login = '';
    this._token = '';
    this._email = '';
    this._avatarUrl = '';
    this._userName = '';
    this._permissions = {} as Record<keyof typeof AuthoritiesConstants, (keyof typeof UPermission)[]>;
    this._expire = 0;
    this._id = 0;
    this.id$.next(0);
    this._groups = [];
    this._isLogin = false;
  }

}
